import { Controller } from "@hotwired/stimulus"
import { csrfToken } from "../../lib/csrf.js"
import { DESTINATION_SIDE, SOURCE_SIDE } from "./mapping_form/constants.js"
import {
  buildMappingValidationPayload,
  buildValidationPayload,
  debounce,
  isMappingReady,
  isValidUnitsPerPack,
  mappedVariantIds,
  mergeProducts,
  resetMappingState,
  resolveVariant,
  unmappedVariants,
} from "./mapping_form/state.js"
import { saveStatusMessage, validationErrorMessage } from "./mapping_form/messages.js"
import { escapeHtml, productRowTemplate, selectionTemplate } from "./mapping_form/templates.js"

export {
  buildMappingValidationPayload,
  buildValidationPayload,
  debounce,
  isMappingReady,
  isValidUnitsPerPack,
  isVariantMapped,
  mappedOptionKeys,
  mappedVariantIds,
  mergeProducts,
  resetMappingState,
  resolveVariant,
  variantOptionKey,
} from "./mapping_form/state.js"
export { compatibleOptionValues } from "./mapping_form/state.js"
export { saveStatusMessage, validationErrorMessage } from "./mapping_form/messages.js"

export default class extends Controller {
  static targets = [
    "sourceSearch",
    "destinationSearch",
    "sourceResults",
    "destinationResults",
    "sourceSelection",
    "sourceSelectAll",
    "sourceSelectAllDelete",
    "destinationSelection",
    "deleteDialog",
    "deleteSource",
    "deleteDestination",
    "save",
    "readiness",
    "unitsPerPack",
    "unitsPerPackError",
    "status",
    "success",
    "errors",
  ]

  static values = {
    sourceProducts: Array,
    destinationProducts: Array,
    sourceTargetProductId: String,
    sourceTargetQuery: String,
    sourceHasMore: Boolean,
    destinationHasMore: Boolean,
    productsUrl: String,
    productDetailUrl: String,
    validateUrl: String,
    saveUrl: String,
  }

  connect() {
    this.initialProducts = {
      [SOURCE_SIDE]: this.sourceProductsValue || [],
      [DESTINATION_SIDE]: this.destinationProductsValue || [],
    }
    this.results = {
      [SOURCE_SIDE]: [...this.initialProducts[SOURCE_SIDE]],
      [DESTINATION_SIDE]: [...this.initialProducts[DESTINATION_SIDE]],
    }
    this.pages = { [SOURCE_SIDE]: 1, [DESTINATION_SIDE]: 1 }
    this.hasMore = { [SOURCE_SIDE]: this.sourceHasMoreValue, [DESTINATION_SIDE]: this.destinationHasMoreValue }
    this.loading = { [SOURCE_SIDE]: false, [DESTINATION_SIDE]: false }
    this.errorsBySide = { [SOURCE_SIDE]: null, [DESTINATION_SIDE]: null }
    this.detailLoading = { [SOURCE_SIDE]: false, [DESTINATION_SIDE]: false }
    this.detailErrors = { [SOURCE_SIDE]: null, [DESTINATION_SIDE]: null }
    this.productDetails = { [SOURCE_SIDE]: null, [DESTINATION_SIDE]: null }
    this.searchControllers = { [SOURCE_SIDE]: null, [DESTINATION_SIDE]: null }
    this.detailControllers = { [SOURCE_SIDE]: null, [DESTINATION_SIDE]: null }
    this.targetProductIds = { [SOURCE_SIDE]: null, [DESTINATION_SIDE]: null }
    this.validationController = null
    this.validationState = { key: null, status: "idle" }
    this.pendingDeleteMappingId = null
    this.pendingDeleteVariantId = null
    this.states = resetMappingState()
    this.searchHandlers = {
      [SOURCE_SIDE]: debounce(() => this.loadProducts(SOURCE_SIDE)),
      [DESTINATION_SIDE]: debounce(() => this.loadProducts(DESTINATION_SIDE)),
    }
    this.renderAll()

    if (this.sourceTargetProductIdValue) {
      this.sourceSearchTarget.value = this.sourceTargetQueryValue
      this.loadShortcutTarget()
    }
  }

  disconnect() {
    Object.values(this.searchHandlers || {}).forEach((handler) => handler.cancel())
    Object.values(this.searchControllers || {}).forEach((controller) => controller?.abort())
    Object.values(this.detailControllers || {}).forEach((controller) => controller?.abort())
    this.validationController?.abort()
  }

  search(event) {
    const handler = this.searchHandlers[event.currentTarget.dataset.mappingSide]
    if (handler) handler()
  }

  async selectProduct(event) {
    const button = event.currentTarget
    await this.selectProductById(button.dataset.mappingSide, button.dataset.mappingProductId)
  }

  async selectProductById(side, productId) {
    const product = this.summaryFor(side, productId)
    if (!product) return

    this.invalidateValidation?.()
    if (side === SOURCE_SIDE) this.resetDestinationState()
    else this.setUnitsPerPack(1)

    this.states[side] = {
      product_id: productId,
      variant: null,
      options: {},
      select_all: side === SOURCE_SIDE,
      has_active_variants: product.active_variant_count > 0,
      ...(side === SOURCE_SIDE ? { mapping_id: null, mapped_variant_ids: [] } : {}),
    }
    this.productDetails[side] = null
    this.detailErrors[side] = null
    this.renderPanel(side)
    this.updateReadiness()
    await this.loadProductDetail(side, productId)
    if (side === SOURCE_SIDE) await this.restoreSavedSourceMapping()
    this.updateReadiness()
    this.validateCurrentMapping()
  }

  async loadShortcutTarget(targetId = this.sourceTargetProductIdValue) {
    const query = this.sourceSearchTarget.value.trim()
    const products = await this.loadProducts(SOURCE_SIDE, { targetProductId: targetId })
    if (!this.element.isConnected || !products || this.errorsBySide[SOURCE_SIDE] || this.sourceSearchTarget.value.trim() !== query) return

    if (!products.some((product) => String(product.id) === String(targetId))) {
      this.setStatus("The requested product could not be found in the title search results.")
      return
    }

    await this.selectProductById(SOURCE_SIDE, targetId)
  }

  async retryProduct(event) {
    const side = event.currentTarget.dataset.mappingSide
    const productId = this.states[side]?.product_id
    if (!productId) return

    await this.loadProductDetail(side, productId)
    if (side === SOURCE_SIDE) await this.restoreSavedSourceMapping()
    this.updateReadiness()
    this.validateCurrentMapping()
  }

  async toggleSelectAll(event) {
    const input = event.currentTarget
    const state = this.states[input.dataset.mappingSide]
    if (!state) return

    const product = this.productFor(input.dataset.mappingSide, state.product_id)
    if (!product) return

    this.invalidateValidation()
    if (input.dataset.mappingSide === SOURCE_SIDE) {
      this.states[SOURCE_SIDE].mapping_id = null
      this.clearDestinationResolution()
    }
    state.select_all = input.checked
    state.variant = state.select_all
      ? null
      : input.dataset.mappingSide === SOURCE_SIDE
        ? unmappedVariants(product)[0] || null
        : resolveVariant(product, {})
    state.options = state.variant ? { ...state.variant.option_values } : {}
    this.renderPanel(input.dataset.mappingSide)
    this.updateReadiness()
    if (input.dataset.mappingSide === SOURCE_SIDE) await this.applySavedMappingForCurrentSource()
    this.validateCurrentMapping()
  }

  async selectOption(event) {
    const input = event.currentTarget
    const side = input.dataset.mappingSide
    const state = this.states[side]
    const product = this.productFor(side, state.product_id)
    if (!state || !product) return

    this.invalidateValidation()
    if (side === SOURCE_SIDE) {
      state.mapping_id = null
      this.clearDestinationResolution()
    }
    state.options[input.dataset.mappingOptionName] = input.value
    state.variant = resolveVariant(product, state.options)
    this.renderPanel(side)
    this.updateReadiness()
    if (side === SOURCE_SIDE && state.variant) await this.applySavedMappingForCurrentSource()
    this.validateCurrentMapping()
  }

  async submit(event) {
    event.preventDefault()
    if (!this.unitsPerPackIsValid()) {
      this.updateUnitsPerPackState()
      this.unitsPerPackTarget.focus()
      return
    }

    if (!isMappingReady(this.states[SOURCE_SIDE], this.states[DESTINATION_SIDE])) {
      this.setStatus("Resolve the source mapping and destination variant before saving.")
      return
    }

    this.saveTarget.disabled = true
    this.setStatus("Saving mapping…")

    try {
      const response = await fetch(this.saveUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": this.csrfToken() },
        body: JSON.stringify({
          ...buildValidationPayload(this.states[SOURCE_SIDE], this.states[DESTINATION_SIDE]),
          units_per_pack: Number(this.unitsPerPackTarget.value),
        }),
      })
      const body = await response.json()

      if (response.ok) {
        const data = body.data || {}
        this.errorsTarget.classList.add("hidden")
        this.errorsTarget.innerHTML = ""
        this.rememberSavedMapping(data.mapping?.id)
        const alreadySaved = data.mapping?.already_saved === true
        const updated = data.mapping?.updated === true
        this.showSuccess(data.summary, alreadySaved, updated)
        this.setStatus(saveStatusMessage(alreadySaved, updated))
      } else {
        const errors = body.error?.details || body.errors
        const message = body.error?.message || "Review the mapping selections."
        this.showErrors(errors || { base: [message] })
        this.setStatus(validationErrorMessage(errors) || message)
        this.updateReadiness()
      }
    } catch {
      this.showErrors({ base: ["Product data is temporarily unavailable. Retry when the database is available."] })
      this.setStatus("Product data is temporarily unavailable.")
      this.updateReadiness()
    }
  }

  rememberSavedMapping(mappingId) {
    if (!mappingId || !this.productDetails[SOURCE_SIDE]) return

    const sourceVariantId = this.states[SOURCE_SIDE].select_all ? null : this.states[SOURCE_SIDE].variant?.id
    const mapping = {
      id: String(mappingId),
      source_variant_id: sourceVariantId,
      destination_product_id: this.states[DESTINATION_SIDE].product_id,
      destination_variant_id: this.states[DESTINATION_SIDE].variant?.id,
      units_per_pack: Number(this.unitsPerPackTarget.value),
    }
    const existing = this.productDetails[SOURCE_SIDE].saved_mappings || []
    const remaining = sourceVariantId === null
      ? existing.filter((candidate) => candidate.source_variant_id === null)
      : existing.filter((candidate) => candidate.source_variant_id !== sourceVariantId)

    this.productDetails[SOURCE_SIDE].saved_mappings = [...remaining, mapping]
    this.states[SOURCE_SIDE].mapping_id = mapping.id
    this.states[SOURCE_SIDE].mapped_variant_ids = mappedVariantIds(this.productDetails[SOURCE_SIDE])
    this.renderPanel(SOURCE_SIDE)
    this.updateReadiness()
  }

  openDeleteDialog(event) {
    event?.preventDefault()
    const trigger = event?.currentTarget
    const mappingId = trigger?.dataset.mappingId
    if (!mappingId || !this.hasDeleteDialogTarget) return

    this.pendingDeleteMappingId = mappingId
    this.pendingDeleteVariantId = trigger.dataset.mappingVariantId || null
    const sourceProduct = this.productDetails[SOURCE_SIDE]
    const destinationProduct = this.productDetails[DESTINATION_SIDE]
    const sourceVariant = sourceProduct?.variants?.find((variant) => String(variant.id) === String(this.pendingDeleteVariantId))
    if (this.hasDeleteSourceTarget) {
      this.deleteSourceTarget.textContent = `${sourceProduct?.title || "Source product"} · ${sourceVariant?.title || "All active variants"}`
    }
    if (this.hasDeleteDestinationTarget) {
      this.deleteDestinationTarget.textContent = `${destinationProduct?.title || "Destination product"} · ${this.states[DESTINATION_SIDE].variant?.title || "Selected variant"}`
    }

    this.deleteDialogTarget.showModal()
  }

  closeDeleteDialog() {
    if (this.hasDeleteDialogTarget && this.deleteDialogTarget.open) this.deleteDialogTarget.close()
  }

  async confirmDelete(event) {
    event?.preventDefault()
    const mappingId = this.pendingDeleteMappingId
    if (!mappingId) return

    const button = event?.currentTarget
    if (button) button.disabled = true
    this.invalidateValidation?.()
    this.setStatus("Deleting mapping…")

    try {
      const response = await fetch(`${this.saveUrlValue}/${encodeURIComponent(mappingId)}`, {
        method: "DELETE",
        headers: { Accept: "application/json", "X-CSRF-Token": this.csrfToken?.() || "" },
      })
      const body = response.json ? await response.json() : {}
      if (!response.ok) throw new Error(body.error?.message || "The mapping could not be deleted.")

      const sourceProduct = this.productDetails[SOURCE_SIDE]
      if (sourceProduct) {
        sourceProduct.saved_mappings = (sourceProduct.saved_mappings || []).filter((mapping) => String(mapping.id) !== String(mappingId))
        this.states[SOURCE_SIDE].mapped_variant_ids = mappedVariantIds(sourceProduct)
      } else {
        this.states[SOURCE_SIDE].mapped_variant_ids = []
      }
      if (String(this.states[SOURCE_SIDE].mapping_id) === String(mappingId)) {
        this.states[SOURCE_SIDE].mapping_id = null
        this.states[SOURCE_SIDE].select_all = false
        this.states[SOURCE_SIDE].variant = null
        this.states[SOURCE_SIDE].options = {}
        this.resetDestinationState()
      }
      this.pendingDeleteMappingId = null
      this.pendingDeleteVariantId = null
      this.renderPanel(SOURCE_SIDE)
      this.updateReadiness()
      this.closeDeleteDialog()
      this.setStatus("Mapping deleted successfully.")
    } catch (error) {
      this.showErrors?.({ base: [error.message] })
      this.setStatus(error.message)
      this.updateReadiness()
    } finally {
      if (button) button.disabled = false
    }
  }

  reset(event) {
    event?.preventDefault()
    Object.values(this.searchHandlers || {}).forEach((handler) => handler.cancel())
    Object.values(this.searchControllers || {}).forEach((controller) => controller?.abort())
    Object.values(this.detailControllers || {}).forEach((controller) => controller?.abort())
    this.invalidateValidation?.()
    this.states = resetMappingState()
    this.pendingDeleteMappingId = null
    this.pendingDeleteVariantId = null
    this.results = {
      [SOURCE_SIDE]: [...this.initialProducts[SOURCE_SIDE]],
      [DESTINATION_SIDE]: [...this.initialProducts[DESTINATION_SIDE]],
    }
    this.pages = { [SOURCE_SIDE]: 1, [DESTINATION_SIDE]: 1 }
    this.hasMore = { [SOURCE_SIDE]: this.sourceHasMoreValue, [DESTINATION_SIDE]: this.destinationHasMoreValue }
    this.loading = { [SOURCE_SIDE]: false, [DESTINATION_SIDE]: false }
    this.errorsBySide = { [SOURCE_SIDE]: null, [DESTINATION_SIDE]: null }
    this.detailLoading = { [SOURCE_SIDE]: false, [DESTINATION_SIDE]: false }
    this.detailErrors = { [SOURCE_SIDE]: null, [DESTINATION_SIDE]: null }
    this.productDetails = { [SOURCE_SIDE]: null, [DESTINATION_SIDE]: null }
    this.sourceSearchTarget.value = ""
    this.destinationSearchTarget.value = ""
    this.unitsPerPackTarget.value = "1"
    this.updateUnitsPerPackState()
    this.successTarget.classList.add("hidden")
    this.successTarget.innerHTML = ""
    this.errorsTarget.classList.add("hidden")
    this.errorsTarget.innerHTML = ""
    this.setStatus("")
    this.renderAll()
  }

  loadMore(event) {
    const side = event.currentTarget.dataset.mappingSide
    if (!side || this.loading[side] || !this.hasMore[side]) return

    const remaining = event.currentTarget.scrollHeight - event.currentTarget.scrollTop - event.currentTarget.clientHeight
    if (remaining > 80) return

    this.loadProducts(side, { page: this.pages[side] + 1, append: true })
  }

  retryProducts(event) {
    const side = event.currentTarget.dataset.mappingSide
    const targetProductId = this.targetProductIds?.[side]
    if (side === SOURCE_SIDE && targetProductId) {
      this.loadShortcutTarget(targetProductId)
      return
    }

    this.loadProducts(side)
  }

  async loadProducts(side, { page = 1, append = false, targetProductId = null } = {}) {
    if (append && (this.loading[side] || !this.hasMore[side])) return []

    this.searchControllers[side]?.abort()
    const controller = new AbortController()
    this.searchControllers[side] = controller
    const query = this[`${side}SearchTarget`].value.trim()
    this.targetProductIds ||= {}
    this.targetProductIds[side] = targetProductId
    this.loading[side] = true
    this.errorsBySide[side] = null
    this.renderProducts(side)

    const url = new URL(this.productsUrlValue, window.location.origin)
    url.searchParams.set("side", side)
    url.searchParams.set("q", query)
    url.searchParams.set("page", page)
    if (targetProductId) url.searchParams.set("target_product_id", targetProductId)

    try {
      const response = await fetch(url, { headers: { Accept: "application/json" }, signal: controller.signal })
      const body = await response.json()
      if (!response.ok) throw new Error(body.error?.message || body.error || "Product data is temporarily unavailable.")

      const incoming = body.data || []
      this.results[side] = append ? mergeProducts(this.results[side], incoming) : incoming
      this.pages[side] = body.meta?.page || page
      this.hasMore[side] = body.meta?.has_more === true
      return incoming
    } catch (error) {
      if (error.name !== "AbortError") {
        this.errorsBySide[side] = error.message
        this.setStatus(error.message)
      }
      return error.name === "AbortError" ? null : []
    } finally {
      if (this.searchControllers[side] === controller) {
        this.loading[side] = false
        this.renderProducts(side)
      }
    }
  }

  async loadProductDetail(side, productId, { variantId = null } = {}) {
    this.detailControllers[side]?.abort()
    const controller = new AbortController()
    this.detailControllers[side] = controller
    this.detailLoading[side] = true
    this.detailErrors[side] = null
    this.renderPanel(side)

    const url = `${this.productDetailUrlValue}/${encodeURIComponent(productId)}?side=${encodeURIComponent(side)}`
    try {
      const response = await fetch(url, { headers: { Accept: "application/json" }, signal: controller.signal })
      const body = await response.json()
      if (!response.ok) throw new Error(body.error?.message || body.error || "Product data is temporarily unavailable.")
      if (String(this.states[side].product_id) !== String(productId)) return

      const product = body.data
      this.productDetails[side] = product
      this.results ||= {}
      this.results[side] = mergeProducts(this.results[side] || [], [
        { id: product.id, title: product.title, image_url: product.image_url, active_variant_count: product.active_variant_count },
      ])
      this.states[side].has_active_variants = product.variants.some((variant) => variant.active)
      if (side === SOURCE_SIDE) this.states[side].mapped_variant_ids = mappedVariantIds(product)
      this.states[side].variant = variantId
        ? product.variants.find((variant) => variant.active && variant.id === String(variantId)) || null
        : side === SOURCE_SIDE && this.states[side].select_all
          ? null
          : side === SOURCE_SIDE
            ? unmappedVariants(product)[0] || null
            : resolveVariant(product, {})
      this.states[side].options = this.states[side].variant ? { ...this.states[side].variant.option_values } : {}
    } catch (error) {
      if (error.name !== "AbortError") this.detailErrors[side] = error.message
    } finally {
      if (this.detailControllers[side] === controller) {
        this.detailLoading[side] = false
        this.renderPanel(side)
        this.updateReadiness()
      }
    }
  }

  clearDestinationResolution() {
    this.states[DESTINATION_SIDE] = {
      product_id: null,
      variant: null,
      options: {},
      select_all: false,
      has_active_variants: false,
    }
    this.productDetails[DESTINATION_SIDE] = null
    this.detailErrors[DESTINATION_SIDE] = null
    this.renderPanel(DESTINATION_SIDE)
  }

  resetDestinationState() {
    this.clearDestinationResolution()
    this.setUnitsPerPack(1)
  }

  setUnitsPerPack(value) {
    this.unitsPerPackTarget.value = String(value || 1)
    this.updateUnitsPerPackState()
  }

  savedMappingForSource(sourceVariantId) {
    const mappings = this.productDetails[SOURCE_SIDE]?.saved_mappings || []
    return mappings.find((mapping) => mapping.source_variant_id === sourceVariantId) || null
  }

  async restoreSavedSourceMapping() {
    const product = this.productFor(SOURCE_SIDE, this.states[SOURCE_SIDE].product_id)
    const mappings = product?.saved_mappings || []
    const mapping = mappings.find((candidate) => candidate.source_variant_id === null) || mappings[0]
    if (!mapping) return

    const defaultVariant = unmappedVariants(product)[0]
    if (defaultVariant && mappings.some((candidate) => candidate.source_variant_id !== null)) {
      this.states[SOURCE_SIDE].select_all = false
      this.states[SOURCE_SIDE].mapping_id = null
      this.states[SOURCE_SIDE].variant = defaultVariant
      this.states[SOURCE_SIDE].options = { ...defaultVariant.option_values }
      this.renderPanel(SOURCE_SIDE)
      return
    }

    const variant = mapping.source_variant_id
      ? product.variants.find((candidate) => candidate.id === mapping.source_variant_id && candidate.active)
      : null
    if (mapping.source_variant_id && !variant) return

    this.states[SOURCE_SIDE].select_all = mapping.source_variant_id === null
    this.states[SOURCE_SIDE].mapping_id = mapping.id
    this.states[SOURCE_SIDE].variant = variant
    this.states[SOURCE_SIDE].options = variant ? { ...variant.option_values } : {}
    this.renderPanel(SOURCE_SIDE)
    await this.applySavedMapping(mapping)
  }

  async applySavedMappingForCurrentSource() {
    const sourceState = this.states[SOURCE_SIDE]
    const sourceVariantId = sourceState.variant?.id
    const mapping = sourceState.select_all
      ? this.savedMappingForSource(null)
      : sourceVariantId
        ? this.savedMappingForSource(sourceVariantId)
        : null
    if (!mapping) {
      this.states[SOURCE_SIDE].mapping_id = null
      this.resetDestinationState()
      return
    }

    await this.applySavedMapping(mapping)
  }

  async applySavedMapping(mapping) {
    this.states[SOURCE_SIDE].mapping_id = mapping.id
    this.states[DESTINATION_SIDE] = {
      product_id: mapping.destination_product_id,
      variant: null,
      options: {},
      select_all: false,
      has_active_variants: true,
    }
    this.productDetails[DESTINATION_SIDE] = null
    this.detailErrors[DESTINATION_SIDE] = null
    this.renderPanel(DESTINATION_SIDE)
    await this.loadProductDetail(DESTINATION_SIDE, mapping.destination_product_id, {
      variantId: mapping.destination_variant_id,
    })
    this.setUnitsPerPack(mapping.units_per_pack)
  }

  invalidateValidation() {
    this.validationController?.abort()
    this.validationController = null
    this.validationState = { key: null, status: "idle" }
    this.hideErrors()
    this.hideSuccess()
  }

  currentValidationKey() {
    if (!isMappingReady(this.states[SOURCE_SIDE], this.states[DESTINATION_SIDE]) || !this.unitsPerPackIsValid()) return null

    return JSON.stringify(buildMappingValidationPayload(this.states[SOURCE_SIDE], this.states[DESTINATION_SIDE]))
  }

  async validateCurrentMapping() {
    const key = this.currentValidationKey()
    if (!key || !this.validateUrlValue) return
    if (this.validationState.key === key && this.validationState.status !== "idle") return

    this.validationController?.abort()
    const controller = new AbortController()
    this.validationController = controller
    this.validationState = { key, status: "loading" }
    this.setStatus("Validating mapping…")
    this.updateReadiness()

    try {
      const response = await fetch(this.validateUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": this.csrfToken() },
        body: JSON.stringify(JSON.parse(key)),
        signal: controller.signal,
      })
      const body = await response.json()
      if (this.currentValidationKey() !== key) return

      if (response.ok) {
        this.validationState = { key, status: "valid" }
        this.hideErrors()
        this.setStatus("Mapping ready to save.")
      } else {
        this.validationState = { key, status: "invalid" }
        const errors = body.error?.details || body.errors
        const message = body.error?.message || "Review the mapping selections."
        this.showErrors(errors || { base: [message] })
        this.setStatus(validationErrorMessage(errors) || message)
      }
    } catch (error) {
      if (error.name !== "AbortError" && this.currentValidationKey() === key) {
        this.validationState = { key, status: "invalid" }
        this.showErrors({ base: ["Product data is temporarily unavailable. Retry when the database is available."] })
        this.setStatus("Product data is temporarily unavailable.")
      }
    } finally {
      if (this.validationController === controller) {
        this.validationController = null
        this.updateReadiness()
      }
    }
  }

  renderAll() {
    this.renderPanel(SOURCE_SIDE)
    this.renderPanel(DESTINATION_SIDE)
    this.updateReadiness()
  }

  renderPanel(side) {
    this.renderProducts(side)
    const target = this[`${side}SelectionTarget`]
    const state = this.states[side]
    const product = this.productFor(side, state.product_id)
    this.renderSourceSelectAll(product, state, side)
    target.innerHTML = this.selectionTemplate(side, product, state, this.detailLoading[side], this.detailErrors[side])
  }

  renderSourceSelectAll(product, state, side) {
    if (side !== SOURCE_SIDE || !this.hasSourceSelectAllTarget) return

    const container = this.sourceSelectAllTarget
    const input = container.querySelector("input[type='checkbox']")
    const hasActiveVariants = state.has_active_variants || product?.variants.some((variant) => variant.active) || false
    const allVariantsMapping = (product?.saved_mappings || []).find((mapping) => mapping.status !== "DISABLED" && mapping.source_variant_id === null)
    container.classList.toggle("hidden", !hasActiveVariants)
    if (input) input.checked = hasActiveVariants && state.select_all === true

    if (this.hasSourceSelectAllDeleteTarget) {
      const deleteButton = this.sourceSelectAllDeleteTarget
      deleteButton.hidden = !allVariantsMapping
      deleteButton.dataset.mappingId = allVariantsMapping?.id || ""
      deleteButton.dataset.mappingVariantId = ""
      deleteButton.dataset.action = "click->pages--mapping-form#openDeleteDialog"
    }
  }

  renderProducts(side) {
    const target = this[`${side}ResultsTarget`]
    const state = this.states[side]
    const products = this.results[side] || []
    const empty = target.querySelector(`[data-mapping-empty-state="${side}-no-results"]`)
    const loading = target.querySelector(`[data-mapping-products-loading="${side}"]`)
    const error = target.querySelector(`[data-mapping-products-error="${side}"]`)
    const errorMessage = target.querySelector(`[data-mapping-products-error-message="${side}"]`)

    const rows = target.querySelector(`[data-mapping-product-rows="${side}"]`)
    if (rows) rows.innerHTML = products.map((product) => productRowTemplate(product, side, String(product.id) === String(state.product_id))).join("")
    if (empty) empty.hidden = products.length > 0 || this.loading[side] || Boolean(this.errorsBySide[side])
    if (loading) loading.classList.toggle("hidden", !this.loading[side])
    if (error) error.classList.toggle("hidden", !this.errorsBySide[side])
    if (errorMessage && this.errorsBySide[side]) errorMessage.textContent = this.errorsBySide[side]
  }

  updateReadiness() {
    const mappingReady = isMappingReady(this.states[SOURCE_SIDE], this.states[DESTINATION_SIDE])
    const unitsValid = this.unitsPerPackIsValid()
    const validationReady = this.validationState.status === "valid" && this.validationState.key === this.currentValidationKey()
    const ready = mappingReady && unitsValid && validationReady

    this.saveTarget.disabled = !ready
    this.saveTarget.setAttribute("aria-disabled", ready ? "false" : "true")
    this.readinessTarget.dataset.state = ready ? "ready" : "incomplete"
    this.readinessTarget.textContent = !unitsValid
      ? "Enter at least 1 unit per pack to continue."
      : mappingReady
      ? ""
      : "Resolve the source mapping and destination variant to continue."
  }

  validateUnitsPerPack() {
    this.updateUnitsPerPackState()
    this.updateReadiness()
  }

  updateUnitsPerPackState() {
    const valid = this.unitsPerPackIsValid()
    const message = this.unitsPerPackTarget.value === ""
      ? "Enter at least 1 unit."
      : "Enter a whole number of 1 or more."

    this.unitsPerPackTarget.setAttribute("aria-invalid", valid ? "false" : "true")
    this.unitsPerPackErrorTarget.textContent = valid ? "" : message
    this.unitsPerPackErrorTarget.classList.toggle("hidden", valid)
  }

  unitsPerPackIsValid() {
    return isValidUnitsPerPack(this.unitsPerPackTarget.value)
  }

  showSuccess(summary, alreadySaved = false, updated = false) {
    this.hideErrors()
    const status = saveStatusMessage(alreadySaved, updated)
    const detail = `${summary.source_product} · ${summary.source_variant} → ${summary.destination_product} · ${summary.destination_variant} · ${summary.units_per_pack} units per pack`
    this.successTarget.innerHTML = `<strong>${escapeHtml(status)}</strong> ${escapeHtml(detail)}`
    this.successTarget.classList.remove("hidden")
    globalThis.RubyUI?.toast?.success(status)
    this.successTarget.scrollIntoView?.({ behavior: "smooth", block: "center" })
  }

  showErrors(errors) {
    this.hideSuccess()
    const message = validationErrorMessage(errors) || "Review the mapping selections and try again."
    this.errorsTarget.textContent = `There is a problem with this mapping: ${message}`
    this.errorsTarget.classList.remove("hidden")
    this.errorsTarget.focus()
  }

  hideErrors() {
    if (!this.hasErrorsTarget) return

    this.errorsTarget.classList.add("hidden")
    this.errorsTarget.innerHTML = ""
  }

  hideSuccess() {
    if (!this.hasSuccessTarget) return

    this.successTarget.classList.add("hidden")
    this.successTarget.innerHTML = ""
  }

  setStatus(message) {
    this.statusTarget.textContent = message
  }

  productFor(side, id) {
    return this.productDetails[side] && String(this.productDetails[side].id) === String(id) ? this.productDetails[side] : null
  }

  summaryFor(side, id) {
    return (this.results[side] || []).find((product) => String(product.id) === String(id)) || null
  }

  csrfToken() {
    return csrfToken()
  }

  selectionTemplate(side, product, state, loading = false, error = null) {
    return selectionTemplate(side, product, state, loading, error)
  }
}
